import React, { useState, useCallback, useMemo, useEffect } from 'react';
import BreederRequestModal from './BreederRequestModal';
import { useParticipants } from '../hooks/useParticipants';
import { getHandlerRequestsByBreederId, sendHandlerRequest, cancelHandlerRequest, updateHandlerRequest } from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';

const BIRD_STATUS_OPTIONS = ['active', 'injured', 'lost', 'died'];
const BIRD_SEX_OPTIONS = ['Hen', 'Cock'];
const normalizeBirdCondition = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return BIRD_STATUS_OPTIONS.includes(normalized) ? normalized : 'active';
};

const normalizeBirdSexOption = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';

  if (normalized === 'h' || normalized === 'hen' || normalized === 'female' || normalized === 'f') {
    return 'Hen';
  }

  if (normalized === 'c' || normalized === 'cock' || normalized === 'male' || normalized === 'm') {
    return 'Cock';
  }

  return '';
};

function BirdInfoModal({ show, request, birds, onSaveBirds, onClose, getHandlerName }) {
  const birdsRequested = request?.numberOfBirds || request?.birdsRequested || 0;
  // Always show the correct number of rows (pre-populate with placeholders if needed)
  const [editBirds, setEditBirds] = useState(() => {
    if (!request) return [];
    const arr = Array.from({ length: birdsRequested }, (_, i) =>
      birds[i]
        ? {
            ...birds[i],
            sex: normalizeBirdSexOption(
              birds[i].sex ?? birds[i].Sex ?? birds[i].gender ?? birds[i].Gender ?? birds[i].birdSex ?? birds[i].BirdSex
            ),
            birdStatus: normalizeBirdCondition(birds[i].birdStatus ?? birds[i].status),
            status: birds[i].status ?? birds[i].birdStatus ?? 'placeholder'
          }
        : {
            bandNumber: '',
            org: '',
            serialNumber: '',
            clubCode: '',
            year: '',
            color: '',
            sex: '',
            birdStatus: 'active',
            status: 'placeholder',
          }
    );
    return arr;
  });

  useEffect(() => {
    if (!request) return;
    setEditBirds(
      Array.from({ length: birdsRequested }, (_, i) =>
        birds[i]
          ? {
              ...birds[i],
              sex: normalizeBirdSexOption(
                birds[i].sex ?? birds[i].Sex ?? birds[i].gender ?? birds[i].Gender ?? birds[i].birdSex ?? birds[i].BirdSex
              ),
              birdStatus: normalizeBirdCondition(birds[i].birdStatus ?? birds[i].status),
              status: birds[i].status ?? birds[i].birdStatus ?? 'placeholder'
            }
          : {
              bandNumber: '',
              org: '',
              serialNumber: '',
              clubCode: '',
              year: '',
              color: '',
              sex: '',
              birdStatus: 'active',
              status: 'placeholder',
            }
      )
    );
  }, [request, birds, birdsRequested]);

  if (!show || !request) return null;

  // Clear field on focus only if it contains placeholder text, force uppercase on input
  const handleBirdChange = (idx, field, value) => {
    if (field === 'birdStatus') {
      const normalizedStatus = normalizeBirdCondition(value);
      setEditBirds(prev => prev.map((b, i) => i === idx
        ? { ...b, birdStatus: normalizedStatus, status: normalizedStatus }
        : b
      ));
      return;
    }

    const normalized = field === 'sex' ? value : value.toUpperCase();
    setEditBirds(prev => prev.map((b, i) => i === idx ? { ...b, [field]: normalized } : b));
  };

  const handleFieldFocus = (idx, field) => {
    setEditBirds(prev => prev.map((b, i) => {
      if (i === idx) {
        const currentValue = b[field] || '';
        // Only clear if it's the placeholder text
        if (currentValue === '[To be added]' || currentValue === '') {
          return { ...b, [field]: '' };
        }
      }
      return b;
    }));
  };

  const handleSave = () => {
    const normalizedBirds = editBirds.map((bird) => {
      const normalizedStatus = normalizeBirdCondition(bird?.birdStatus ?? bird?.status);
      return {
        ...bird,
        birdStatus: normalizedStatus,
        status: normalizedStatus,
      };
    });

    onSaveBirds(request.id, normalizedBirds);
    onClose();
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
        <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header bg-info text-white">
            <h5 className="modal-title">
              <i className="bi bi-plus-circle me-2"></i>
              Add/Edit Bird Information
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
            <div className="row mb-3">
              <div className="col-md-6"><strong>Handler:</strong> {getHandlerName(request)}</div>
              <div className="col-md-6"><strong>Birds Requested:</strong> {birdsRequested}</div>
              <div className="col-md-6"><strong>Request Date:</strong> {new Date(request.requestDate).toLocaleDateString()}</div>
              <div className="col-md-6">
                <strong>Status:</strong> <span className={`badge ${
                  request.status === 'approved' ? 'bg-success' :
                  request.status === 'pending' ? 'bg-warning' : 'bg-secondary'
                }`}>{request.status}</span>
              </div>
            </div>
            <hr />
            <h6 className="fw-bold mb-3"><i className="bi bi-list-stars me-2"></i>Bird Information</h6>
            
            {/* Desktop Table View */}
            <div className="d-none d-md-block table-responsive">
              <table className="table table-striped">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Band Number</th>
                    <th>Color</th>
                    <th>Sex</th>
                    <th>Bird Status</th>
                  </tr>
                </thead>
                <tbody>
                  {editBirds.map((bird, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <input type="text" className="form-control" value={bird.bandNumber}
                          onChange={e => handleBirdChange(idx, 'bandNumber', e.target.value)}
                          onFocus={() => handleFieldFocus(idx, 'bandNumber')}
                        />
                      </td>
                      <td>
                        <input type="text" className="form-control" value={bird.color}
                          onChange={e => handleBirdChange(idx, 'color', e.target.value)}
                          onFocus={() => handleFieldFocus(idx, 'color')}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={normalizeBirdSexOption(bird.sex)}
                          onChange={e => handleBirdChange(idx, 'sex', e.target.value)}
                        >
                          <option value="">Select sex</option>
                          {BIRD_SEX_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={normalizeBirdCondition(bird.birdStatus ?? bird.status)}
                          onChange={e => handleBirdChange(idx, 'birdStatus', e.target.value)}
                        >
                          {BIRD_STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>
                              {opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-md-none">
              {editBirds.map((bird, idx) => (
                <div key={idx} className="card mb-3 border">
                  <div className="card-header bg-light">
                    <strong>Bird #{idx + 1}</strong>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label small fw-bold">Band Number</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={bird.bandNumber}
                        onChange={e => handleBirdChange(idx, 'bandNumber', e.target.value)}
                        onFocus={() => handleFieldFocus(idx, 'bandNumber')}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small fw-bold">Color</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={bird.color}
                        onChange={e => handleBirdChange(idx, 'color', e.target.value)}
                        onFocus={() => handleFieldFocus(idx, 'color')}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small fw-bold">Sex</label>
                      <select
                        className="form-select"
                        value={normalizeBirdSexOption(bird.sex)}
                        onChange={e => handleBirdChange(idx, 'sex', e.target.value)}
                      >
                        <option value="">Select sex</option>
                        {BIRD_SEX_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-0">
                      <label className="form-label small fw-bold">Bird Status</label>
                      <select
                        className="form-select"
                        value={normalizeBirdCondition(bird.birdStatus ?? bird.status)}
                        onChange={e => handleBirdChange(idx, 'birdStatus', e.target.value)}
                      >
                        {BIRD_STATUS_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button type="button" className="btn btn-success ms-2" onClick={handleSave} disabled={editBirds.length === 0}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BreederTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate, 
  isEditing, 
  onEditingChange, 
  saving, 
  setSaving 
}) {
  // State hooks
  const [breederData, setBreederData] = useState({ totalBirds: '', handlerRequests: [] });
  const [originalBreederData, setOriginalBreederData] = useState(null);
  const [isTotalBirdsSaved, setIsTotalBirdsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedHandler, setSelectedHandler] = useState(null);
  const [showAdminRotationModal, setShowAdminRotationModal] = useState(false);
  const [adminHandler, setAdminHandler] = useState(null);

  // Bird Info modal helpers
  // SaveBirdInfo: pure state update for adding a bird to a handlerRequest
  // Save all bird info edits at once
  // SaveBirdInfo: persist bird info to backend and update local state
  const SaveBirdInfo = async (requestId, newBirdsArray) => {
    console.log('SaveBirdInfo called:', { requestId, newBirdsArray });
    if (!requestId || !Array.isArray(newBirdsArray)) {
      console.error('Invalid params:', { requestId, newBirdsArray });
      return;
    }
    
    // Find the current request to get all its data
    const currentRequest = breederData.handlerRequests.find(r => r.id === requestId);
    if (!currentRequest) {
      console.error('Request not found:', requestId);
      if (typeof onMessage === 'function') {
        onMessage('Request not found', 'error');
      }
      return;
    }
    
    // Prepare the updated request with new bird details
    const updatedRequest = {
      ...currentRequest,
      birdDetails: newBirdsArray
    };
    
    try {
      // Call the backend to update the entire handler request
      console.log('Calling updateHandlerRequest...', updatedRequest);
      const result = await updateHandlerRequest(requestId, updatedRequest);
      console.log('updateHandlerRequest result:', result);
      
      if (result && result.success) {
        // Update local state with the new bird details
        setBreederData(prev => ({
          ...prev,
          handlerRequests: prev.handlerRequests.map(r =>
            r.id === requestId ? { ...r, birdDetails: newBirdsArray } : r
          )
        }));
        
        // Reload participant data to get fresh data from server
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
        
        if (typeof onMessage === 'function') {
          onMessage('Bird details saved successfully!', 'success');
        }
      } else {
        console.error('Failed to save bird details:', result?.message);
        if (typeof onMessage === 'function') {
          onMessage(`Failed to save bird details: ${result?.message || 'Unknown error'}`, 'error');
        }
      }
    } catch (err) {
      console.error('SaveBirdInfo error:', err);
      if (typeof onMessage === 'function') {
        onMessage(`Failed to save bird details: ${err.message || 'Unknown error'}`, 'error');
      }
    }
  };

  // getBirdsInfo: get birds array by requestId
  const getBirdsInfo = (requestId) => {
    const req = breederData.handlerRequests.find(r => r.id === requestId);
    return req && Array.isArray(req.birdDetails) ? req.birdDetails : [];
  };
  // Get all participants for handlers
  const { participants: allParticipants } = useParticipants();

  // Initialize breeder data
  useEffect(() => {
    if (participant) {
      const initData = {
        totalBirds: participant.breederInfo?.totalBirdsForRace || participant.totalBirds || '',
        handlerRequests: participant.handlerRequests || []
      };
      setBreederData(initData);
      if (!originalBreederData) {
        setOriginalBreederData(initData);
      }
      
      // Mark as saved if participant already has total birds
      if (participant.breederInfo?.totalBirdsForRace || participant.totalBirds) {
        setIsTotalBirdsSaved(true);
      }
      
      // Reset unsaved changes when loading fresh data
      setHasUnsavedChanges(false);
    }
  }, [participant]);

  // Helper functions
  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  // Memoize expensive calculations that depend on breeder data
  const getRequiredAdminRotationBirds = useMemo(() => {
    const totalBirds = parseInt(breederData.totalBirds) || 0;
    if (totalBirds >= 30) return 3;
    if (totalBirds >= 12) return 2;
    if (totalBirds >= 5) return 1;
    return 0;
  }, [breederData.totalBirds]);

  const getAvailableForHandlerRequests = useMemo(() => {
    const totalBirds = parseInt(breederData.totalBirds) || 0;
    return totalBirds - getRequiredAdminRotationBirds;
  }, [breederData.totalBirds, getRequiredAdminRotationBirds]);

  // Memoize handler-specific bird calculations
  const getApprovedBirdsForHandler = useCallback((handlerId) => {
    return breederData.handlerRequests
      .filter(r => r.handlerId === handlerId && r.status === 'approved')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getPendingBirdsForHandler = useCallback((handlerId) => {
    return breederData.handlerRequests
      .filter(r => r.handlerId === handlerId && r.status === 'pending')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getApprovedAndPendingBirdsForHandler = useCallback((handlerId) => {
    return getApprovedBirdsForHandler(handlerId) + getPendingBirdsForHandler(handlerId);
  }, [getApprovedBirdsForHandler, getPendingBirdsForHandler]);

  const getRemainingBirdSlotsForHandler = useCallback((handlerId) => {
    return 5 - getApprovedAndPendingBirdsForHandler(handlerId);
  }, [getApprovedAndPendingBirdsForHandler]);

  // Memoize total bird calculations
  const getTotalApprovedBirds = useMemo(() => {
    return breederData.handlerRequests
      .filter(r => r.status === 'approved')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getTotalPendingBirds = useMemo(() => {
    return breederData.handlerRequests
      .filter(r => r.status === 'pending')
      .reduce((total, r) => total + (r.numberOfBirds || r.birdsRequested || 0), 0);
  }, [breederData.handlerRequests]);

  const getTotalRequestedRotationBirds = useMemo(() => {
    const rotationRequests = breederData.handlerRequests
      .filter(r => (r.handlerId === 'admin-rotation' || r.handlerId === 'admin-rotation-mandatory' || r.isAdminRotation === true) && (r.status === 'pending' || r.status === 'approved'));
    
    return rotationRequests.reduce((total, r) => total + (r.birdsRequested || r.numberOfBirds || 0), 0);
  }, [breederData.handlerRequests]);

  // Memoize mandatory rotation status
  const getMandatoryRotationStatus = useMemo(() => {
    const requiredBirds = getRequiredAdminRotationBirds;
    if (requiredBirds === 0) return { status: 'none', birds: 0 };

    const mandatoryRequest = breederData.handlerRequests
      .find(r => r.handlerId === 'admin-rotation-mandatory');

    if (!mandatoryRequest) {
      return { status: 'needed', birds: requiredBirds };
    }

    return {
      status: mandatoryRequest.status, // 'pending' or 'approved'
      birds: mandatoryRequest.numberOfBirds || mandatoryRequest.birdsRequested || 0
    };
  }, [getRequiredAdminRotationBirds, breederData.handlerRequests]);




  const [showBirdModal, setShowBirdModal] = useState(false);
  const [selectedRequestForBirds, setSelectedRequestForBirds] = useState(null);
  const handleViewBirdInfo = useCallback((request) => {
    setSelectedRequestForBirds(request);
    setShowBirdModal(true);
  }, []);
  const handleCloseBirdModal = () => {
    setShowBirdModal(false);
    setSelectedRequestForBirds(null);
  };
  const getHandlerName = (request) => {
    if (!request) return 'Unknown Handler';
    if (request.handlerId === 'admin-rotation' || request.handlerId === 'admin-rotation-mandatory' || request.isAdminRotation === true) {
      return 'Admin Rotation';
    }
    const handler = allParticipants?.find(p => p.id === request.handlerId);
    return handler ? handler.fullName : 'Unknown Handler';
  };

  // Memoize remaining birds calculation
  const getRemainingBirdsForHandlers = useMemo(() => {
    const availableForHandlers = getAvailableForHandlerRequests;
    const alreadyAssignedOrPending = getTotalApprovedBirds + getTotalPendingBirds;
    const rotationRequests = getTotalRequestedRotationBirds;
    return Math.max(0, availableForHandlers - alreadyAssignedOrPending - rotationRequests);
  }, [getAvailableForHandlerRequests, getTotalApprovedBirds, getTotalPendingBirds, getTotalRequestedRotationBirds]);

  const calculateBirds = useMemo(() => {
    const total = parseInt(breederData.totalBirds) || 0;
    const requiredRotation = getRequiredAdminRotationBirds;
    const toRotation = requiredRotation; // Automatically use required rotation
    const availableForHandlers = getAvailableForHandlerRequests;
    const approvedBirds = getTotalApprovedBirds;
    const rotationRequests = getTotalRequestedRotationBirds;
    // Calculation: Total - Rotation - Approved = Un-assigned
    const remainingForHandlers = Math.max(0, total - toRotation - approvedBirds);
    
    return {
      total,
      toRotation,
      requiredRotation,
      availableForHandlers,
      approvedBirds,
      remainingForHandlers,
      isValid: total >= 5
    };
  }, [breederData.totalBirds, getRequiredAdminRotationBirds, getAvailableForHandlerRequests, getTotalApprovedBirds, getTotalRequestedRotationBirds]);

  const getAvailableHandlers = useMemo(() => {
    if (!allParticipants || !participant) return [];
    
    return allParticipants.filter(p => {
      const hasHandlerRole = Array.isArray(p.role) 
        ? p.role.some(r => r.toLowerCase() === 'handler')
        : p.role?.toLowerCase() === 'handler';
      
      const isInSameSection = p.section === participant.section;
      const isNotSelf = p.id !== participant.id;
      
      return hasHandlerRole && isInSameSection && isNotSelf;
    }).sort((a, b) => {
      const nameA = (a.fullName || '').toLowerCase();
      const nameB = (b.fullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allParticipants, participant]);

  const getRequestStatus = useCallback((handlerId) => {
    const request = breederData.handlerRequests.find(r => r.handlerId === handlerId);
    // Treat cancelled requests as if no request exists (allow new requests)
    if (!request || request.status === 'cancelled'|| request.status === 'rejected') {
      return 'none';
    }
    return request.status;
  }, [breederData.handlerRequests]);

  // Event handlers
  const handleInputChange = (field, value) => {
    if (!isEditing) return;
    
    setBreederData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  const handleSendRequest = async (requestData) => {
    try {
      setLoadingRequests(true);
      
      // Check if total birds are saved - be more flexible here
      const totalBirds = participant.totalBirds || participant.breederInfo?.totalBirdsForRace || breederData.totalBirds;
      if (!totalBirds || totalBirds < 5) {
        onMessage('Please save your total birds count (minimum 5) before sending requests.', 'error');
        return;
      }
      
      // Validate max 5 birds per handler (pending + approved)
      const currentTotal = getApprovedAndPendingBirdsForHandler(requestData.handlerId);
      const requested = Number(requestData.numberOfBirds) || 0;
      
      if (currentTotal + requested > 5) {
        onMessage('You cannot send more than 5 birds to the same handler (including pending and approved requests).', 'error');
        return;
      }
      
      // Validate total birds don't exceed available slots
      const remainingSlots = getRemainingBirdsForHandlers;
      
      if (requested > remainingSlots) {
        onMessage(`You can only request ${remainingSlots} more birds for handlers. ${getRequiredAdminRotationBirds()} birds must go to admin rotation.`, 'error');
        return;
      }
      
      // Validate request data before sending
      if (!requestData.handlerId) {
        onMessage('Handler ID is required', 'error');
        return;
      }
      if (!requestData.numberOfBirds || requestData.numberOfBirds <= 0) {
        onMessage('Number of birds must be greater than 0', 'error');
        return;
      }
      
      const result = await sendHandlerRequest(participant.id, requestData.handlerId, requestData);
      
      if (result.success) {
        onMessage('Request sent successfully! The handler will review your request.', 'success');
        
        // Reload breeder requests from server to get the actual data
        await loadBreederRequests(true); // Force reload
        
        // Reload participant data to update slot counts like original dashboard
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
        
        setShowRequestModal(false);
        setSelectedHandler(null);
      } else {
        onMessage(`Failed to send request: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      onMessage('Failed to send handler request. Please try again.', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this handler request?')) {
      return;
    }

    try {
      setLoadingRequests(true);
      
      // Call API to cancel the request (calls UpdateHandlerRequestStatus with status='cancelled')
      const result = await cancelHandlerRequest(participant.id, requestId);
      
      if (result.success) {
        onMessage('Request cancelled successfully!', 'success');
        
        // Reload requests from server to get updated data
        await loadBreederRequests(true);
        
        // Reload participant data to update available birds count
        if (typeof onParticipantUpdate === 'function') {
          await onParticipantUpdate();
        }
      } else {
        onMessage(`Failed to cancel request: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      onMessage('Failed to cancel request. Please try again.', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle admin rotation click from summary - automatically send the required rotation birds
  const handleAdminRotationClickFromSummary = async () => {
    try {
      setLoadingRequests(true);
      
      const rotationBirds = getRequiredAdminRotationBirds;
      
      // Send request directly with the calculated rotation birds
      const result = await sendHandlerRequest(participant.id, 'admin-rotation-mandatory', {
        numberOfBirds: rotationBirds,
        isAdminRotation: true,
        IsMandatoryRotation: true // This is a mandatory rotation, cannot be cancelled
      });
      
      if (result && result.success) {
        onMessage(`Successfully sent ${rotationBirds} birds to admin rotation! An administrator will review and assign your birds.`);
        await loadBreederRequests(); // Reload requests
      } else {
        onMessage(`Failed to send admin rotation request: ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      onMessage(`An error occurred while sending the admin rotation request: ${errorMsg}`);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle admin rotation click from section - open modal for custom amount
  const handleAdminRotationClick = () => {
    // Create a virtual admin handler for rotation
    const adminRotationHandler = {
      id: 'admin-rotation',
      fullName: 'Admin Rotation',
      loftName: 'System Placement',
      handlerInfo: {
        section: 'ROTATION',
        totalBirds: 999999, // No limit
        reserved: 0,
        openSlots: 999999
      }
    };
    
    setAdminHandler(adminRotationHandler);
    setShowAdminRotationModal(true);
  };

  // Handle admin rotation request submission
  const handleSubmitAdminRotation = async (requestData) => {
    try {
      // Send request to admin-rotation ID with isAdminRotation flag
      const result = await sendHandlerRequest(participant.id, 'admin-rotation', {
        ...requestData,
        isAdminRotation: true
      });
      
      if (result && result.success) {
        onMessage('Admin rotation request sent successfully! An administrator will review and assign your birds.');
        await loadBreederRequests(); // Reload requests
        setShowAdminRotationModal(false);
        setAdminHandler(null);
      } else {
        onMessage(`Failed to send admin rotation request: ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      onMessage(`An error occurred while sending the admin rotation request: ${errorMsg}`);
    }
  };

  // Only save breeder profile info (totalBirds) here, not handler requests
  const handleSave = useCallback(async () => {
    if (!isEditing) return;
    try {
      setSaving(true);
      const birdCalc = calculateBirds;
      if (!birdCalc.isValid) {
        if (birdCalc.total < 5) {
          onMessage('Minimum 5 birds required for race participation.', 'error');
        } else {
          onMessage('Bird numbers are invalid.', 'error');
        }
        setSaving(false);
        return;
      }
      // Only update totalBirds (and breederInfo) for participant
      // Handler requests are managed by handler request APIs only
      // Call onParticipantUpdate to refresh data if needed
      setOriginalBreederData(breederData);
      setIsTotalBirdsSaved(true);
      setHasUnsavedChanges(false);
      onEditingChange(false);
      onMessage('Breeder bird count saved locally. (Server save logic can be added if needed)', 'success');
      if (typeof onParticipantUpdate === 'function') {
        await onParticipantUpdate();
      }
    } catch (error) {
      onMessage('Failed to save breeder information. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }, [isEditing, breederData, onParticipantUpdate, onMessage, onEditingChange, setSaving, setOriginalBreederData, setIsTotalBirdsSaved, setHasUnsavedChanges, calculateBirds]);

  // Load breeder requests from server
  const loadBreederRequests = useCallback(async (force = false) => {
    if (!participant?.id) return;
    if (loadingRequests && !force) {
      return;
    }
    
    try {
      setLoadingRequests(true);
      const requestsResult = await getHandlerRequestsByBreederId(participant.id);

      let requests = [];
      if (requestsResult && Array.isArray(requestsResult.requests)) {
        requests = requestsResult.requests;
      } else if (Array.isArray(requestsResult)) {
        requests = requestsResult;
      }
      
      // Trust server response - no client-side filtering needed
      setBreederData(prev => ({
        ...prev,
        handlerRequests: requests
      }));
      setRequestsLoaded(true);
    } catch (error) {
    } finally {
      setLoadingRequests(false);
    }
  }, [participant?.id, loadingRequests]);

  // Cache role check to prevent unnecessary re-renders
  const isBreeder = useMemo(() => {
    return hasRole('breeder');
  }, [participant?.role]);

  // Load requests when component mounts or participant changes (only once per participant)
  useEffect(() => {
    if (participant?.id && isBreeder && !requestsLoaded) {
      loadBreederRequests();
    }
  }, [participant?.id, isBreeder, requestsLoaded, loadBreederRequests]);

  // Reset requests loaded flag when participant changes
  useEffect(() => {
    setRequestsLoaded(false);
  }, [participant?.id]);

  // Set up global save function for ModernDashboard
  useEffect(() => {
    window.breederTabSave = handleSave;
    return () => {
      delete window.breederTabSave;
    };
  }, [handleSave]);

  const handleCancel = () => {
    setBreederData(originalBreederData);
    setIsTotalBirdsSaved(!!participant.totalBirds);
    setHasUnsavedChanges(false);
    // Don't call onEditingChange here - let global system handle it
  };

  const toggleRowExpanded = (handlerId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(handlerId)) {
      newExpanded.delete(handlerId);
    } else {
      newExpanded.add(handlerId);
    }
    setExpandedRows(newExpanded);
  };
  // Mandatory rotation request logic
const mandatoryRotationRequest = breederData.handlerRequests.find(
  r => r.handlerId === 'admin-rotation-mandatory'
);
const mandatoryRotationStatus = mandatoryRotationRequest?.status || 'none';
const requiredRotationBirds = getRequiredAdminRotationBirds;

const handleMandatoryRotationRequest = async () => {
  await sendHandlerRequest(
    participant.id,
    'admin-rotation-mandatory',
    {
      numberOfBirds: requiredRotationBirds,
      isAdminRotation: true,
      IsMandatoryRotation: true
    }
  );
  // Optionally reload requests and show a message
  await loadBreederRequests(true);
  onMessage('Mandatory rotation request sent to admin.', 'success');
};

  // Memoized calculations - must be before early return
  const birdCalc = calculateBirds;
  const pendingRequests = useMemo(() => breederData.handlerRequests.filter(r => r.status === 'pending'), [breederData.handlerRequests]);
  const approvedRequests = useMemo(() => breederData.handlerRequests.filter(r => r.status === 'approved'), [breederData.handlerRequests]);

  // Bird Distribution Dashboard (Breeder)
  const totalBirds = parseInt(breederData.totalBirds) || 0;

  // Calculate mandatory and admin rotation birds
  let mandatoryRotation = 0;
  if (totalBirds >= 30) mandatoryRotation = 3;
  else if (totalBirds >= 12) mandatoryRotation = 2;
  else if (totalBirds >= 5) mandatoryRotation = 1;

  const adminRotation = breederData.handlerRequests
    .filter(r =>
      (r.handlerId === 'admin-rotation' || r.isAdminRotation === true) &&
      !r.IsMandatoryRotation // not the mandatory rotation request
    )
    .reduce((sum, r) => sum + (r.numberOfBirds || r.birdsRequested || 0), 0);

  // Birds approved by handlers (not admin rotation)
  const approved = breederData.handlerRequests
    .filter(r => r.status === 'approved' && !r.isAdminRotation)
    .reduce((sum, r) => sum + (r.numberOfBirds || r.birdsRequested || 0), 0);
  // Birds pending with handlers (not admin rotation)
  const pending = breederData.handlerRequests
    .filter(r => r.status === 'pending' && !r.isAdminRotation)
    .reduce((sum, r) => sum + (r.numberOfBirds || r.birdsRequested || 0), 0);

  // Birds that still require handler assignment
  const assignedOrPending = approved + pending + mandatoryRotation + adminRotation;
  const birdsNeedingHandler = Math.max(0, totalBirds - assignedOrPending);

  if (!hasRole('breeder')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Breeder role to access this section. You can add the Breeder role in the Personal Info tab.
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      {/* Bird Distribution Dashboard (Breeder) */}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-bar-chart-fill me-2"></i>
            Bird Distribution (Breeder)
          </h5>
        </div>
        <div className="card-body">
          <div className="row text-center">
            <div className="col">
              <div className="fw-bold small">Total Entered</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{totalBirds}</div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Birds you plan to enter</div>
            </div>
            <div className="col">
              <div className="fw-bold small">Approved by Handler</div>
              <div className="text-success" style={{ fontSize: '1.5rem', fontWeight: 600 }}>{approved}</div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Birds approved by handlers</div>
            </div>
            <div className="col">
              <div className="fw-bold small">Pending</div>
              <div className="text-warning" style={{ fontSize: '1.5rem', fontWeight: 600 }}>{pending}</div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Birds in pending requests</div>
            </div>
            <div className="col">
              <div className="fw-bold small">Rotation Birds</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.2 }}>
                <span className="text-primary">{mandatoryRotation}</span>
                <span className="small text-muted"> (Mandatory)</span>
                <br />
                <span className="text-info">{adminRotation}</span>
                <span className="small text-muted"> (Admin Choice)</span>
              </div>
            </div>
            <div className="col">
              <div className="fw-bold small">Need Handler</div>
              <div className="text-danger" style={{ fontSize: '1.5rem', fontWeight: 600 }}>{birdsNeedingHandler}</div>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Still require handler</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-list-check me-2"></i>
            My Handler Requests
          </h6>
        </div>
        <div className="card-body">
          {breederData.handlerRequests.length === 0 ? (
            <div className="text-center text-muted py-3">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No handler requests sent yet</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 border rounded">
                    <thead className="table-dark">
                      <tr>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-person-workspace me-2"></i>
                          Handler
                        </th>
                        <th className="text-nowrap text-center py-3" style={{width: '100px'}}>
                          <i className="bi bi-egg me-1"></i>
                          Birds
                        </th>
                        <th className="text-nowrap text-center py-3" style={{width: '100px'}}>
                          <i className="bi bi-check-circle me-1"></i>
                          Status
                        </th>
                        <th className="d-none d-md-table-cell text-nowrap text-center py-3" style={{width: '120px'}}>
                          <i className="bi bi-calendar me-1"></i>
                          Date Sent
                        </th>
                        <th className="text-center py-3" style={{width: '180px'}}>
                          <i className="bi bi-gear me-1"></i>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                  {breederData.handlerRequests.map(request => {
                    const handler = allParticipants?.find(p => p.id === request.handlerId);
                    const handlerName = (request.handlerId === 'admin-rotation' || request.handlerId === 'admin-rotation-mandatory' || request.isAdminRotation === true) 
                      ? 'Admin Rotation' 
                      : (handler?.fullName || 'Unknown Handler');
                    return (
                      <tr key={request.id} className="align-middle">
                        <td>
                          <div>
                            <strong className="text-nowrap">
                              {handlerName}
                              {(request.IsMandatoryRotation === true || request.isMandatoryRotation === true) && handlerName === 'Admin Rotation' && ' (Mandatory)'}
                            </strong>
                            <div className="d-md-none small text-muted mt-1">
                              📅 {new Date(request.requestDate).toLocaleDateString()}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-primary">{request.numberOfBirds}</span>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${
                            request.status === 'pending' ? 'bg-warning' :
                            request.status === 'approved' ? 'bg-success' :
                            request.status === 'cancelled' ? 'bg-secondary' :
                            'bg-danger'
                          }`}>
                            {request.status === 'cancelled' ? 'Cancelled' : request.status}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell text-center text-nowrap">{new Date(request.requestDate).toLocaleDateString()}</td>
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center">

                            
                            {/* Always show Add Bird Info for approved requests or mandatory rotation requests */}
                            {(request.status === 'approved' || request.IsMandatoryRotation || request.isMandatoryRotation) && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleViewBirdInfo(request)}
                                title="Add/edit bird information"
                              >
                                <i className="bi bi-plus-circle"></i>
                                <span className="d-none d-lg-inline ms-1">View/Edit Birds</span>
                              </button>
                            )}
                            {/* Only show Cancel button for pending requests that are NOT mandatory */}
                            {request.status === 'pending' && !request.IsMandatoryRotation && !request.isMandatoryRotation && (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleCancelRequest(request.id)}
                                disabled={loadingRequests}
                                title="Cancel this request"
                              >
                                <i className="bi bi-x-circle"></i>
                                <span className="d-none d-sm-inline ms-1">Cancel</span>
                              </button>
                            )}

                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="align-middle">
                <td>
                  <strong className="text-nowrap">Admin Rotation (Mandatory)</strong>
                </td>
                <td className="text-center">
                  <span className="badge bg-primary">{requiredRotationBirds}</span>
                </td>
                <td colSpan={2} className="text-center align-middle">
                  <div className="small text-muted">This request is for mandatory rotation and will be sent to the admin for assignment.</div>
                </td>
                <td className="text-center">
                  {/* Button removed as requested */}
                </td>
              </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {breederData.handlerRequests.map(request => {
                  const handler = allParticipants?.find(p => p.id === request.handlerId);
                  const handlerName = (request.handlerId === 'admin-rotation' || request.handlerId === 'admin-rotation-mandatory' || request.isAdminRotation === true) 
                    ? 'Admin Rotation' 
                    : (handler?.fullName || 'Unknown Handler');
                  return (
                    <div key={request.id} className="border-bottom">
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">
                              {handlerName}
                              {(request.IsMandatoryRotation === true || request.isMandatoryRotation === true) && handlerName === 'Admin Rotation' && ' (Mandatory)'}
                            </h6>
                            <div className="small text-muted">
                              📅 {new Date(request.requestDate).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-end">
                            <span className={`badge ${
                              request.status === 'pending' ? 'bg-warning' :
                              request.status === 'approved' ? 'bg-success' :
                              request.status === 'cancelled' ? 'bg-secondary' :
                              'bg-danger'
                            } mb-1`}>
                              {request.status === 'cancelled' ? 'Cancelled' : request.status}
                            </span>
                            <div>
                              <span className="badge bg-primary">{request.numberOfBirds} birds</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="d-flex flex-column gap-2">
                          {(request.status === 'approved' || request.IsMandatoryRotation || request.isMandatoryRotation) && (
                            <button
                              className="btn btn-outline-primary w-100"
                              onClick={() => handleViewBirdInfo(request)}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              View/Edit Birds
                            </button>
                          )}
                          {request.status === 'pending' && !request.IsMandatoryRotation && !request.isMandatoryRotation && (
                            <button
                              className="btn btn-outline-danger w-100"
                              onClick={() => handleCancelRequest(request.id)}
                              disabled={loadingRequests}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Cancel Request
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                  {/* Admin Rotation (Mandatory) Card */}
                  <div className="border-bottom">
                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold">Admin Rotation (Mandatory)</h6>
                          <div className="small text-muted">
                            📅 {mandatoryRotationRequest?.requestDate ? new Date(mandatoryRotationRequest.requestDate).toLocaleDateString() : '-'}
                          </div>
                        </div>
                        <div className="text-end">
                          <span className={`badge ${
                            mandatoryRotationStatus === 'pending' ? 'bg-warning' :
                            mandatoryRotationStatus === 'approved' ? 'bg-success' :
                            'bg-secondary'
                          } mb-1`}>
                            {mandatoryRotationStatus.charAt(0).toUpperCase() + mandatoryRotationStatus.slice(1)}
                          </span>
                          <div>
                            <span className="badge bg-primary">{requiredRotationBirds} birds</span>
                          </div>
                        </div>
                      </div>
                      {/* Description for Admin Rotation (Mandatory) to match desktop */}
                      <div className="small text-muted mt-2">This request is for mandatory rotation and will be sent to the admin for assignment.</div>
                      {/* Actions intentionally omitted for Admin Rotation (Mandatory) to match desktop */}
                      <div className="d-flex flex-column gap-2"></div>
                    </div>
                  </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Available Handlers */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h3 className="mb-0" style={{ color: '#0b3d91' }}>
            Available Handlers
          </h3>
          <span className="badge bg-primary">{' '} {getAvailableHandlers.length} </span>
        </div>
        <div className="card-body">
          {getAvailableHandlers.length === 0 ? (
            <div className="text-center text-muted py-3">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No handlers available in your section</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 border rounded">
                    <thead className="table-primary">
                      <tr>
                        <th style={{width: '50px'}} className="py-3"></th>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-person me-2"></i>
                          Handler Name
                        </th>
                        <th className="d-none d-md-table-cell text-nowrap py-3">
                          <i className="bi bi-house me-1"></i>
                          Loft Name
                        </th>
                        <th className="d-none d-lg-table-cell text-nowrap py-3">
                          <i className="bi bi-geo-alt me-1"></i>
                          Section
                        </th>
                        <th className="d-none d-lg-table-cell text-nowrap py-3">
                          <i className="bi bi-ruler me-1"></i>
                          Distance (mi)
                        </th>
                        <th className="text-nowrap py-3">
                          <i className="bi bi-info-circle me-1"></i>
                          Availability
                        </th>
                        <th className="text-nowrap py-3" style={{width: '150px'}}>
                          <i className="bi bi-tools me-1"></i>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                  {getAvailableHandlers.map(handler => {
                    const requestStatus = getRequestStatus(handler.id);
                    const isExpanded = expandedRows.has(handler.id);
                    return (
                      <React.Fragment key={handler.id}>
                        <tr className="align-middle">
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
                              onClick={() => toggleRowExpanded(handler.id)}
                              title={isExpanded ? 'Hide details' : 'Show more details'}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                              <span className="d-none d-md-inline small">
                                {isExpanded ? 'Less' : 'Details'}
                              </span>
                            </button>
                          </td>
                          <td>
                            <div>
                              <strong className="text-nowrap">{handler.fullName}</strong>
                              <div className="d-md-none small text-muted mt-1">
                                {handler.loftName && <div>🏠 {handler.loftName}</div>}
                                <div>📍 Section {handler.handlerInfo?.section || 'N/A'} • {(() => {
                                  const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                  if (!distance) return 'No distance data';
                                  return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                })()}</div>
                              </div>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell text-nowrap">{handler.loftName || '-'}</td>
                          <td className="d-none d-lg-table-cell text-center">{handler.handlerInfo?.section || '-'}</td>
                          <td className="d-none d-lg-table-cell text-nowrap">{(() => {
                            const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                            if (!distance) return '-';
                            return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                          })()}</td>
                          <td className="text-center">
                            {requestStatus === 'pending' && (
                              <span className="badge bg-warning text-dark px-3 py-2">
                                <i className="bi bi-clock me-1"></i>
                                Pending
                              </span>
                            )}
                            {requestStatus === 'approved' && (
                              <span className="badge bg-success px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i>
                                Approved
                              </span>
                            )}
                            {requestStatus === 'none' && (
                              <span className="text-success fw-medium px-3 py-2">
                                <i className="bi bi-plus-circle me-1"></i>
                                Available
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {requestStatus === 'none' && (
                                <button
                                  className="btn btn-outline-primary btn-sm text-nowrap"
                                  onClick={() => {
                                    setSelectedHandler(handler);
                                    setShowRequestModal(true);
                                  }}
                                  disabled={loadingRequests || !isTotalBirdsSaved || getRemainingBirdSlotsForHandler(handler.id) <= 0 || getRemainingBirdsForHandlers <= 0}
                                  title={!isTotalBirdsSaved ? 'Save total birds first' : getRemainingBirdSlotsForHandler(handler.id) <= 0 ? 'Handler has reached 5 bird limit' : getRemainingBirdsForHandlers <= 0 ? 'No more birds available for handlers' : 'Send request to this handler'}
                                >
                                  <i className="bi bi-envelope me-1"></i>
                                  <span className="d-none d-sm-inline">Send Request</span>
                                  <span className="d-sm-none">Send</span>
                                </button>
                              )}
                              
                              {requestStatus === 'pending' && (
                                <button
                                  className="btn btn-outline-danger btn-sm text-nowrap"
                                  onClick={() => {
                                    const request = breederData.handlerRequests.find(r => r.handlerId === handler.id);
                                    if (request) handleCancelRequest(request.id);
                                  }}
                                  disabled={loadingRequests}
                                >
                                  <i className="bi bi-x-circle me-1"></i>
                                  <span className="d-none d-sm-inline">Cancel</span>
                                  <span className="d-sm-none">×</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td></td>
                            <td colSpan="6">
                              <div className="bg-light p-3 rounded">
                                <div className="row">
                                  <div className="col-md-6">
                                    <strong>Contact Information:</strong>
                                    <div className="mt-2">
                                      <div><i className="bi bi-envelope me-2"></i>{handler.emailAddress || 'No email provided'}</div>
                                      <div><i className="bi bi-phone me-2"></i>{handler.mobile || 'No mobile provided'}</div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <strong>Additional Info:</strong>
                                    <div className="mt-2">
                                      <div><i className="bi bi-geo-alt me-2"></i>Section: {handler.handlerInfo?.section || 'Not specified'}</div>
                                      <div><i className="bi bi-speedometer2 me-2"></i>Distance: {(() => {
                                        const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                        if (!distance) return 'Not specified';
                                        return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                      })()}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {getAvailableHandlers.map(handler => {
                  const requestStatus = getRequestStatus(handler.id);
                  const isExpanded = expandedRows.has(handler.id);
                  return (
                    <div key={handler.id} className="border-bottom">
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{handler.fullName}</h6>
                            <div className="small text-muted mb-1">{handler.loftName || '-'}</div>
                            <div className="small">
                              📍 {handler.handlerInfo?.section || '-'} • 📏 {(() => {
                                const distance = handler.handlerInfo?.distanceFromRaceStation || handler.handlerInfo?.distance;
                                if (!distance) return 'N/A';
                                return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '') + ' mi';
                              })()} 
                            </div>
                          </div>
                          <div className="text-end">
                            {requestStatus === 'pending' && (
                              <span className="badge bg-warning text-dark px-3 py-2">
                                <i className="bi bi-clock me-1"></i>
                                Pending
                              </span>
                            )}
                            {requestStatus === 'approved' && (
                              <span className="badge bg-success px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i>
                                Approved
                              </span>
                            )}
                            {requestStatus === 'none' && (
                              <span className="text-success fw-medium px-3 py-2">
                                <i className="bi bi-plus-circle me-1"></i>
                                Available
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          <button
                            className="btn btn-outline-info flex-grow-1"
                            onClick={() => toggleRowExpanded(handler.id)}
                            style={{ minHeight: '44px' }}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                            {isExpanded ? 'Hide' : 'Show'} Details
                          </button>
                          
                          {requestStatus === 'pending' && (
                            <button className="btn btn-outline-warning flex-grow-1" disabled style={{ minHeight: '44px' }}>
                              <i className="bi bi-clock me-1"></i>
                              Pending Request
                            </button>
                          )}
                          {requestStatus === 'approved' && (
                            <button className="btn btn-outline-success flex-grow-1" disabled style={{ minHeight: '44px' }}>
                              <i className="bi bi-check-circle me-1"></i>
                              Approved
                            </button>
                          )}
                          {requestStatus === 'none' && (
                            <button
                              className="btn btn-primary flex-grow-1"
                              onClick={() => {
                                setSelectedHandler(handler);
                                setShowRequestModal(true);
                              }}
                              disabled={loadingRequests || !isTotalBirdsSaved || getRemainingBirdSlotsForHandler(handler.id) <= 0 || getRemainingBirdsForHandlers <= 0}
                              title={!isTotalBirdsSaved ? 'Save total birds first' : getRemainingBirdSlotsForHandler(handler.id) <= 0 ? 'Handler has reached 5 bird limit' : getRemainingBirdsForHandlers <= 0 ? 'No more birds available for handlers' : 'Send request to this handler'}
                              style={{ minHeight: '44px' }}
                            >
                              <i className="bi bi-envelope me-1"></i>
                              Send Request
                            </button>
                          )}
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-3 p-3 bg-light rounded">
                            <div className="mb-3">
                              <div className="small fw-bold text-muted mb-2">CONTACT INFORMATION</div>
                              <div className="small">
                                <div className="mb-1">
                                  <i className="bi bi-envelope me-2"></i>
                                  {handler.emailAddress || 'No email provided'}
                                </div>
                                <div>
                                  <i className="bi bi-phone me-2"></i>
                                  {handler.mobile || 'No mobile provided'}
                                </div>
                              </div>
                            </div>
                            {handler.handlerInfo?.breederApplicationMethod && (
                              <div>
                                <div className="small fw-bold text-muted mb-2">CONTACT METHODS</div>
                                <div className="d-flex gap-1 flex-wrap">
                                  <span className="badge bg-primary">In-App</span>
                                  {handler.handlerInfo.breederApplicationMethod.includes('text') && (
                                    <span className="badge bg-info">Text</span>
                                  )}
                                  {handler.handlerInfo.breederApplicationMethod.includes('email') && (
                                    <span className="badge bg-info">Email</span>
                                  )}
                                  {handler.handlerInfo.breederApplicationMethod.includes('call') && (
                                    <span className="badge bg-info">Call</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Admin Rotation Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-arrow-repeat me-2"></i>
            Admin Rotation (System Placement)
          </h6>
        </div>
        <div className="card-body">
          <div className="list-group">
            <div className="list-group-item" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0b3d91' }}>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center">
                <div className="flex-grow-1 mb-3 mb-lg-0">
                  <strong>
                    <i className="bi bi-arrow-repeat me-2" style={{ color: '#0b3d91' }}></i>
                    Admin Rotation (System Placement)
                  </strong>
                  <div className="small mt-1">
                    <span className="text-muted">No handler preference - Admin will assign</span>
                    <span className="d-none d-sm-inline mx-2">|</span>
                    <span className="d-sm-none d-block mt-1"></span>
                    <span className="text-info fw-bold">No Bird Limits</span>
                  </div>
                  <div className="small text-muted mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    Select this option if you want administrators to assign your birds to available handlers
                  </div>
                </div>
                <button 
                  className="btn btn-sm text-nowrap align-self-stretch align-self-lg-auto"
                  style={{ backgroundColor: '#0b3d91', color: 'white', minWidth: '140px' }}
                  onClick={handleAdminRotationClick}
                  disabled={loadingRequests || !isTotalBirdsSaved}
                  title={!isTotalBirdsSaved ? 'Save total birds first' : 'Request admin rotation'}
                >
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Request Rotation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && selectedHandler && (
        <BreederRequestModal
          show={showRequestModal}
          handler={selectedHandler}
          breeder={participant}
          onSubmit={handleSendRequest}
          onClose={() => {
            setShowRequestModal(false);
            setSelectedHandler(null);
          }}
          maxBirds={Math.min(
            getRemainingBirdSlotsForHandler(selectedHandler.id),
            getRemainingBirdsForHandlers
          )}
          approvedBirdsWithHandler={getApprovedBirdsForHandler(selectedHandler.id)}
        />
      )}

      {/* Admin Rotation Modal */}
      {showAdminRotationModal && adminHandler && (
        <BreederRequestModal
          show={showAdminRotationModal}
          handler={adminHandler}
          breeder={participant}
          onSubmit={handleSubmitAdminRotation}
          onClose={() => {
            setShowAdminRotationModal(false);
            setAdminHandler(null);
          }}
          maxBirds={999} // No limit for admin rotation
          approvedBirdsWithHandler={0}
        />
      )}

      {/* Bird Details Modal */}
      <BirdInfoModal
        show={showBirdModal}
        request={selectedRequestForBirds}
        birds={selectedRequestForBirds ? getBirdsInfo(selectedRequestForBirds.id) : []}
        onSaveBirds={SaveBirdInfo}
        onClose={handleCloseBirdModal}
        getHandlerName={getHandlerName}
      />
    </div>
  );
}
